import React, { useContext } from "react";
import {
  Badge,
  Box,
  Card,
  Cell,
  Layout,
  Loader,
  Page,
  Text,
  Tooltip,
  WixDesignSystemProvider,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import * as Icons from "@wix/wix-ui-icons-common";
import "./index.css";
import { withProviders } from "../withProviders";

import TrialExpiryAlert from "../../components/trial-expiry-alert";
import SubscriptionContext from "../../contexts/subscription-context";
import SyncHealthChart from "../../components/charts/SyncHealthChart";
import { useFaireStoreDetails } from "../../hooks/faire/useFaire";
import { useWixStoreDetails } from "../../hooks/wix/useWix";

const Index = () => {
  /*
  |--------------------------------------------------------------------------
  | Get Site Details
  |--------------------------------------------------------------------------
  |
  */
  const { isTrialStarted, isPaid, currentPlan } = useContext(SubscriptionContext);

  /*
  |--------------------------------------------------------------------------
  | Initial State Data
  |--------------------------------------------------------------------------
  |
  */
  const initialData = {
    total_products: 0,
    active_products: 0,
    total_categories: 0,
    unlinked_products: 0,
    linked_products: 0,
    lastExported: null,
  };

  /*  
  |--------------------------------------------------------------------------
  | Fetch Faire Store Data
  |--------------------------------------------------------------------------
  |
  */
  const { faireData, isLoading: isFaireDetailsLoading } = useFaireStoreDetails(initialData);

  /*
  |--------------------------------------------------------------------------
  | Fetch Wix Store Data
  |--------------------------------------------------------------------------
  |
  */
  const { wixData, isLoading: isWixDetailsLoading } = useWixStoreDetails(initialData);

  // Check Source
  const checkSource = (source?: string) => {
    let isLoading = false;

    if (source === "wix") {
      return (isLoading = isWixDetailsLoading);
    }

    return (isLoading = isFaireDetailsLoading);
  };

  /*
  |--------------------------------------------------------------------------
  | Checks If Total Products Loading 
  |--------------------------------------------------------------------------
  |
  */
  const checkLoadingProducts = (data: any, source?: string) => {
    let isLoading = checkSource(source);

    return isLoading ? (
      <Loader size="tiny" />
    ) : (
      <>
        <Text skin="primary" weight="bold">
          {data.active_products}
        </Text>
        /<Text>{data.total_products}</Text>
      </>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Checks If Linked Products Loading
  |--------------------------------------------------------------------------
  |
  */
  const checkLoadingLinkedProducts = (data: any, source?: string) => {
    let isLoading = checkSource(source);

    return isLoading ? (
      <Loader size="tiny" />
    ) : (
      <>
        <Text skin="primary" weight="bold">
          {data.unlinked_products}
        </Text>
        /<Text>{data.total_products}</Text>
      </>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Checks If Total Categories Loading
  |--------------------------------------------------------------------------
  |
  */
  const checkLoadingCategories = (data: any, source?: string) => {
    let isLoading = checkSource(source);

    return isLoading ? (
      <Loader size="tiny" />
    ) : (
      <>
        <Text>{data?.total_categories}</Text>
      </>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Checks If Total Categories Loading
  |--------------------------------------------------------------------------
  |
  */
  const checkLoadingExportedProducts = (data: any, source?: string) => {
    let isLoading = checkSource(source);

    return isLoading ? (
      <Loader size="tiny" />
    ) : (
      <>
        <Text skin={data?.last_exported ? "primary" : "disabled"} weight="bold">
          {data?.last_exported ? data?.last_exported : "Not Exported"}
        </Text>
      </>
    );
  };

  return (
    <WixDesignSystemProvider>
      <Page height="95vh">
        <Page.Header title="Dashboard" />
        <Page.Content>
          <TrialExpiryAlert />
          <Layout>
            <Cell span={8}>
              <Layout cols={12}>
                <Cell span={12}>
                  <Card>
                    <Card.Header
                      title="Wix Store Overview"
                      subtitle={<Text size="small">Get insights into your Wix Store Products</Text>}
                    ></Card.Header>
                    <Card.Divider />
                    <Card.Content>
                      <Layout gap={2} cols={12}>
                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.UploadExport color="#1067f2" />
                              <Text size="small" weight="bold">
                                Last Exported
                              </Text>
                            </Box>
                            <Text weight="bold" size="small" skin="primary">
                              {checkLoadingExportedProducts(wixData, "wix")}
                            </Text>
                          </Box>
                        </Cell>
                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.Activity color="#1067f2" />
                              <Text size="small" weight="bold">
                                Active Products
                              </Text>
                            </Box>
                            <Tooltip
                              appendTo="window"
                              content={`Inactive Products: ${wixData?.total_products - wixData?.active_products}`}
                            >
                              <div>{checkLoadingProducts(wixData, "wix")}</div>
                            </Tooltip>
                          </Box>
                        </Cell>
                      </Layout>
                      <Layout gap={2} cols={12}>
                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.Archive color="#1067f2" />
                              <Text size="small" weight="bold">
                                Unlinked Products
                              </Text>
                            </Box>
                            <Tooltip appendTo="window" content={`Linked Products: ${wixData.linked_products}`}>
                              <div>{checkLoadingLinkedProducts(wixData, "wix")}</div>
                            </Tooltip>
                          </Box>
                        </Cell>

                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.Category color="#1067f2" />
                              <Text size="small" weight="bold">
                                Total Categories
                              </Text>
                            </Box>
                            <Text size="small" skin="primary">
                              {checkLoadingCategories(wixData, "wix")}
                            </Text>
                          </Box>
                        </Cell>
                      </Layout>
                    </Card.Content>
                  </Card>
                </Cell>
                <Cell span={12}>
                  <Card>
                    <Card.Header
                      title="Faire Store Overview"
                      subtitle={<Text size="small">Get insights into your Faire Store Products</Text>}
                    ></Card.Header>
                    <Card.Divider />
                    <Card.Content>
                      <Layout gap={2} cols={12}>
                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.UploadExport color="#1067f2" />
                              <Text size="small" weight="bold">
                                Last Exported
                              </Text>
                            </Box>
                            <Text size="small" weight="bold" skin="disabled">
                              {checkLoadingExportedProducts(faireData, "faire")}
                            </Text>
                          </Box>
                        </Cell>
                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.Activity color="#1067f2" />
                              <Text size="small" weight="bold">
                                Active Products
                              </Text>
                            </Box>
                            <Tooltip
                              appendTo="window"
                              content={`Inactive Products: ${faireData.total_products - faireData.active_products}`}
                            >
                              <div>{checkLoadingProducts(faireData, "faire")}</div>
                            </Tooltip>
                          </Box>
                        </Cell>
                      </Layout>
                      <Layout gap={2} cols={12}>
                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.Archive color="#1067f2" />
                              <Text size="small" weight="bold">
                                Unlinked Products
                              </Text>
                            </Box>
                            <Tooltip appendTo="window" content={`Linked Products: ${faireData.linked_products}`}>
                              <div>{checkLoadingLinkedProducts(faireData, "faire")}</div>
                            </Tooltip>
                          </Box>
                        </Cell>

                        <Cell span={6}>
                          <Box
                            padding="SP5"
                            margin="SP1"
                            border="1px solid"
                            borderColor="D50"
                            borderRadius={7}
                            align="space-between"
                            gap="SP1"
                          >
                            <Box gap="10px">
                              <Icons.Category color="#1067f2" />
                              <Text size="small" weight="bold">
                                Total Categories
                              </Text>
                            </Box>
                            <Text weight="bold" size="small" skin="primary">
                              {checkLoadingCategories(faireData, "faire")}
                            </Text>
                          </Box>
                        </Cell>
                      </Layout>
                    </Card.Content>
                  </Card>
                </Cell>
              </Layout>
            </Cell>

            <Cell span={4}>
              <Layout cols={12}>
                <Cell span={12}>
                  <Card>
                    <Card.Header title="Account Information"></Card.Header>
                    <Card.Divider />
                    <Card.Content>
                      <Box align="space-between">
                        <Text size="small"> Subscription package </Text>
                        <Text skin="primary" weight="bold" size="small">
                          {currentPlan}
                        </Text>
                      </Box>
                    </Card.Content>
                  </Card>
                </Cell>

                <Cell span={12}>
                  <Card>
                    <Card.Header title="Faire Store Details"></Card.Header>
                    <Card.Divider />
                    <Card.Content>
                      <Box height="130px" direction="vertical" gap="18px">
                        <Box align="space-between">
                          <Text size="small"> Store Name </Text>
                          <Text skin="primary" weight="bold" size="small">
                            {faireData?.store_details?.store_name}
                          </Text>
                        </Box>
                        <Card.Divider />

                        <Box align="space-between">
                          <Text size="small"> Status </Text>
                          <Badge skin="success" size="small">
                            {isTrialStarted && !isPaid ? "on Trial" : isPaid ? "Live" : "-"}
                          </Badge>
                        </Box>
                        <Card.Divider />

                        <Box align="space-between">
                          <Text size="small"> Active Products </Text>
                          <Text skin="primary" weight="bold" size="small">
                            {faireData?.active_products}
                          </Text>
                        </Box>
                      </Box>
                    </Card.Content>
                  </Card>
                </Cell>
              </Layout>
            </Cell>

            {/* <SyncHealthChart wixData={wixData} faireData={wixData} /> */}
          </Layout>
        </Page.Content>
      </Page>
    </WixDesignSystemProvider>
  );
};

export default withProviders(Index);
